import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { t, type Key } from '../i18n';
import { haptic } from '../telegram';
import { compress, enqueue, flush, load, newRef, type Mark } from '../queue';
import { fmt } from './fmt';

type TripT = {
  id: string;
  number: string;
  status: string;
  case_numbers: string[];
  vehicle: { plate: string | null; model: string | null };
  stops: { seq: number; place: string; country: string | null; eta_plan: string | null; etd_plan: string | null }[];
  steps: { key: string; done: boolean; current: boolean }[];
  current: string | null;
};

const PROBLEMS = ['delay', 'documents', 'vehicle', 'other'] as const;
const EXPENSES = ['fuel', 'tolls', 'border', 'other'] as const;

async function sendMark(m: Mark): Promise<void> {
  await api('/tg/app/driver/marks', { body: m });
}

/** Рейс водія: кроки (кнопка + фото), офлайн-черга відміток, «Проблема», чек. */
export function Trip({ tripId }: { tripId: string | null }) {
  const [trip, setTrip] = useState<TripT | null | undefined>(undefined);
  const [photos, setPhotos] = useState<string[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(load().length);
  const [panel, setPanel] = useState<'problem' | 'expense' | null>(null);
  const [cat, setCat] = useState<(typeof EXPENSES)[number]>('fuel');
  const [amount, setAmount] = useState('');
  const [cur, setCur] = useState('UAH');
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api<{ trip: TripT | null }>(`/tg/app/driver/trip${tripId ? `?trip_id=${tripId}` : ''}`);
      setTrip(r.trip);
    } catch {
      setTrip((x) => (x === undefined ? null : x));
    }
  }, [tripId]);

  const sync = useCallback(async () => {
    const r = await flush(sendMark);
    setQueued(r.left);
    if (r.sent) await refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
    sync();
    const on = () => { setOnline(true); sync(); };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const timer = window.setInterval(sync, 20000);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); window.clearInterval(timer); };
  }, [refresh, sync]);

  if (trip === undefined) return <div className="center">{t('loading')}</div>;
  if (!trip) return <div className="center">{t('no_trip')}</div>;

  const cur_ = trip.current;
  const needPhoto = cur_ !== null && cur_ !== 'departure';

  async function done() {
    if (!cur_ || !trip) return;
    if (needPhoto && !photos.length) {
      setMsg(t('photo_needed'));
      haptic('warning');
      return;
    }
    const m: Mark = { client_ref: newRef(), trip_id: trip.id, step_key: cur_, at: new Date().toISOString(), photos };
    setQueued(enqueue(m).length);
    setPhotos([]);
    // оптимістично: крок зараховано на екрані, відмітка піде, щойно буде мережа
    setTrip({ ...trip, steps: trip.steps.map((s) => (s.key === cur_ ? { ...s, done: true, current: false } : s)),
              current: trip.steps.find((s) => !s.done && s.key !== cur_)?.key ?? null });
    haptic('success');
    setMsg(t('step_done'));
    await sync();
  }

  async function addPhoto(f: File | undefined) {
    if (!f) return;
    const data = await compress(f);
    setPhotos((p) => [...p, data].slice(0, 3));
  }

  async function problem(kind: string) {
    try {
      await api('/tg/app/driver/problems', { body: { trip_id: trip!.id, kind } });
      setMsg(t('problem_sent'));
      haptic('success');
    } catch {
      setMsg(t('error'));
    }
    setPanel(null);
  }

  async function expense() {
    const minor = Math.round(parseFloat(amount.replace(',', '.')) * 100);
    if (!minor) return;
    try {
      await api('/tg/app/driver/expenses', { body: { trip_id: trip!.id, category: cat, amount_minor: minor, currency: cur,
                                                     photo: photos[0] ?? null } });
      setMsg(t('saved'));
      setPhotos([]);
      setAmount('');
      setPanel(null);
    } catch {
      setMsg(t('error'));
    }
  }

  const n = trip.steps.findIndex((s) => s.key === cur_) + 1;
  return (
    <div className="app">
      {(!online || queued > 0) && (
        <div className="offline"><span>{online ? t('trip') : t('offline')}</span><span>{queued} {t('in_queue')}</span></div>
      )}
      <div className="body">
        <div>
          <div className="k">{trip.case_numbers.filter(Boolean).join(', ')} · {[trip.vehicle.model, trip.vehicle.plate].filter(Boolean).join(' ')}</div>
          <h1 className="h1">{trip.stops[0]?.place} → {trip.stops[trip.stops.length - 1]?.place}</h1>
          {trip.stops[trip.stops.length - 1]?.eta_plan && (
            <div className="k">{t('deliver_by')} {fmt(trip.stops[trip.stops.length - 1].eta_plan)}</div>
          )}
        </div>
        <div className="tl">
          {trip.steps.map((s, i) => (
            <div className="st" key={s.key}>
              <span className={`n ${s.done ? 'ok' : s.key === cur_ ? 'now' : 'next'}`}>{i + 1}</span>
              <span>
                <b>{t(`s_${s.key}` as Key)}</b>
                {s.key === cur_ && needPhoto && (
                  <div className="slots">
                    {[0, 1, 2].map((j) => (
                      <label key={j} className={`slot ${photos[j] ? 'f' : ''}`} style={photos[j] ? { backgroundImage: `url(${photos[j]})` } : undefined}>
                        {!photos[j] && t('add_photo')}
                        <input type="file" accept="image/*" capture="environment" aria-label={t('add_photo')}
                               onChange={(e) => addPhoto(e.target.files?.[0])} />
                      </label>
                    ))}
                  </div>
                )}
              </span>
            </div>
          ))}
        </div>
        <div className="two">
          <button className="ghost" type="button" onClick={() => setPanel(panel === 'problem' ? null : 'problem')}>{t('problem')}</button>
          <button className="ghost" type="button" onClick={() => setPanel(panel === 'expense' ? null : 'expense')}>{t('receipt')}</button>
        </div>
        {panel === 'problem' && (
          <div className="card two">
            {PROBLEMS.map((p) => <button key={p} className="ghost" type="button" onClick={() => problem(p)}>{t(`p_${p}` as Key)}</button>)}
          </div>
        )}
        {panel === 'expense' && (
          <div className="card">
            <div className="pills">
              {EXPENSES.map((e) => <button key={e} type="button" className={cat === e ? 'on' : ''} onClick={() => setCat(e)}>{t(`e_${e}` as Key)}</button>)}
            </div>
            <label className="slot" style={photos[0] ? { backgroundImage: `url(${photos[0]})` } : undefined}>
              {!photos[0] && t('add_photo')}
              <input type="file" accept="image/*" capture="environment" aria-label={t('receipt')} onChange={(e) => addPhoto(e.target.files?.[0])} />
            </label>
            <div className="price">
              <input inputMode="decimal" placeholder={t('amount')} value={amount} onChange={(e) => setAmount(e.target.value)} aria-label={t('amount')} />
              <select value={cur} onChange={(e) => setCur(e.target.value)} aria-label="currency">
                {['UAH', 'EUR', 'MDL', 'PLN', 'RON', 'USD'].map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <button className="ghost" type="button" onClick={expense}>{t('save')}</button>
          </div>
        )}
      </div>
      {msg && <div className="toast" role="status" onClick={() => setMsg(null)}>{msg}</div>}
      {cur_ ? (
        <button className="main" type="button" onClick={done}>{`${n}. ${t(`s_${cur_}` as Key)} — ${t('step_done')}`}</button>
      ) : (
        <div className="main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{t('all_done')}</div>
      )}
    </div>
  );
}
